import React, { useMemo } from 'react';
import { useAuth } from '@pooflabs/web';
import { PageLayout } from '@/components/poof-ui';
import { Particles } from '@/components/effects';
import WalletButton from '@/components/WalletButton';
import { useCurrentRound } from '@/hooks/use-current-round';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { useLivePythPrices } from '@/hooks/use-live-pyth-prices';
import { subscribeUsers } from '@/lib/collections/users';
import type { UsersResponse } from '@/lib/collections/users';
import type { RoundsRacersResponse } from '@/lib/collections/rounds';
import { LotteryPanel } from './LotteryPanel';
import { RevealPanel } from './RevealPanel';
import { RacePanel } from './RacePanel';
import { FinishedPanel } from './FinishedPanel';
import { Leaderboard } from './Leaderboard';

const PHASE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  lottery: { label: '🎰 LOTTERY', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  reveal: { label: '👀 REVEAL', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  racing: { label: '🏁 RACING', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
  finished: { label: '🏆 FINISHED', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)' },
  waiting: { label: '⏳ WAITING', color: '#a78bfa', bg: 'rgba(167,139,250,0.1)' },
};

export const HomePage: React.FC = () => {
  const { user } = useAuth();

  const {
    round,
    racers,
    picks,
    userPick,
    secondsLeft,
    phase,
    choosingOpen,
    secondsUntilNextRound,
    loading,
  } = useCurrentRound(user?.address);

  const { data: userProfile } = useRealtimeData<UsersResponse | null>(
    subscribeUsers,
    !!user?.address,
    user?.address ?? ''
  );

  const phaseInfo = PHASE_LABELS[phase] ?? PHASE_LABELS.waiting;

  // Collect coin IDs from current racers for live price polling
  const racerCoinIds = useMemo(() => racers.map((r) => r.coinId), [racers]);

  // Poll Pyth Hermes every 5s during reveal and racing phases for visual smoothness
  const livePollingEnabled = phase === 'reveal' || phase === 'racing';
  const { prices: livePrices } = useLivePythPrices(racerCoinIds, livePollingEnabled);

  /**
   * Overlay live Pyth prices onto the DB racer records.
   * - currentPrice: replaced with live price when available
   * - performancePct: recomputed from (livePrice - dbStartPrice) / dbStartPrice * 100
   *   only when dbStartPrice > 0 (i.e., race has started and baseline is set)
   * - During reveal phase startPrice is '0', so we show live price as currentPrice
   *   but keep performancePct at '0' / unchanged from DB.
   */
  const liveRacers: RoundsRacersResponse[] = useMemo(() => {
    if (!livePollingEnabled || Object.keys(livePrices).length === 0) {
      return racers;
    }
    return racers.map((racer) => {
      const livePrice = livePrices[racer.coinId];
      if (livePrice === undefined || livePrice <= 0) return racer;

      const dbStartPrice = parseFloat(racer.startPrice);
      const hasStartPrice = dbStartPrice > 0;

      const livePerfPct = hasStartPrice
        ? (((livePrice - dbStartPrice) / dbStartPrice) * 100).toFixed(2)
        : racer.performancePct;

      return {
        ...racer,
        currentPrice: String(livePrice),
        performancePct: livePerfPct,
      };
    });
  }, [racers, livePrices, livePollingEnabled]);

  return (
    <PageLayout fullBleed footer={false}>
      {/* Background particles */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <Particles quantity={40} color="hsl(326 100% 61%)" />
      </div>

      {/* Racing stripes background accent */}
      <div
        className="fixed top-0 left-0 right-0 h-1 z-10"
        style={{
          background:
            'repeating-linear-gradient(90deg, #ff3da8 0, #ff3da8 20px, #fbbf24 20px, #fbbf24 40px, #34d399 40px, #34d399 60px, #60a5fa 60px, #60a5fa 80px, #a78bfa 80px, #a78bfa 100px)',
        }}
      />

      <div className="relative z-10 container py-4 pb-16">
        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 pt-3">
          <div>
            <h1
              className="text-3xl sm:text-4xl font-bold"
              style={{
                background: 'linear-gradient(90deg, #ff3da8 0%, #fbbf24 50%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              🏁 PumpRace
            </h1>
            <p className="text-sm mt-0.5" style={{ color: '#a78bfa' }}>
              Pick the pumping coin — free to play, earn points!
            </p>
          </div>
          <div className="flex items-center gap-3">
            {round && (
              <div className="text-xs" style={{ color: '#a78bfa' }}>
                Round <strong className="text-white">#{round.roundId}</strong>
              </div>
            )}
            <div
              className="text-xs font-bold px-3 py-1.5 rounded-full"
              style={{
                background: phaseInfo.bg,
                color: phaseInfo.color,
                border: `1.5px solid ${phaseInfo.color}44`,
              }}
            >
              {phaseInfo.label}
            </div>
            <WalletButton />
          </div>
        </header>

        {/* User score strip */}
        {user && (
          <div
            className="mb-5 px-4 py-3 rounded-2xl flex flex-wrap items-center gap-4"
            style={{
              background: 'rgba(26,10,46,0.6)',
              border: '1px solid rgba(251,191,36,0.25)',
            }}
          >
            <div className="text-xs" style={{ color: '#a78bfa' }}>
              🎮 Playing as{' '}
              <span className="font-semibold text-white/80">
                {user.address.slice(0, 6)}...{user.address.slice(-4)}
              </span>
            </div>
            <div className="flex gap-4 ml-auto">
              <div className="text-center">
                <div className="text-lg font-bold" style={{ color: '#fbbf24' }}>
                  {(userProfile?.totalPoints ?? 0).toLocaleString()}
                </div>
                <div className="text-xs" style={{ color: '#a78bfa' }}>points</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold" style={{ color: '#34d399' }}>
                  {userProfile?.totalWins ?? 0}
                </div>
                <div className="text-xs" style={{ color: '#a78bfa' }}>wins</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white/80">
                  {userProfile?.totalRoundsPlayed ?? 0}
                </div>
                <div className="text-xs" style={{ color: '#a78bfa' }}>rounds</div>
              </div>
              {userPick && round && (
                <div className="text-center border-l border-white/10 pl-4">
                  <div className="text-sm font-bold" style={{ color: '#ff3da8' }}>
                    ${racers.find((r) => r.coinId === userPick.coinId)?.symbol ?? '?'}
                  </div>
                  <div className="text-xs" style={{ color: '#a78bfa' }}>your pick</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main panel */}
          <div className="lg:col-span-2">
            <div
              className="rounded-3xl p-5 sm:p-6"
              style={{
                background: 'rgba(15,5,30,0.8)',
                border: '1.5px solid rgba(167,139,250,0.2)',
                backdropFilter: 'blur(12px)',
              }}
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="text-5xl animate-bounce">🏎️</div>
                  <div className="text-lg font-semibold" style={{ color: '#a78bfa' }}>
                    Warming up the track...
                  </div>
                  <div className="text-sm" style={{ color: '#7c6b9c' }}>
                    First race starts within 1 minute
                  </div>
                </div>
              ) : !round ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  {phase === 'racing' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin" style={{ width: 28, height: 28, color: '#a78bfa' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-5xl">🏎️</span>
                      </div>
                      <div className="text-lg font-semibold" style={{ color: '#a78bfa' }}>
                        Setting up the next race...
                      </div>
                      <div className="text-sm" style={{ color: '#7c6b9c' }}>
                        Race data is loading — this only takes a few seconds
                      </div>
                    </>
                  ) : phase === 'reveal' ? (
                    <>
                      <div className="text-5xl" style={{ animation: 'pulse 1.2s ease-in-out infinite' }}>👀</div>
                      <div className="text-lg font-semibold" style={{ color: '#34d399' }}>
                        Coins selected!
                      </div>
                      <div className="text-sm" style={{ color: '#7c6b9c' }}>
                        Race starting in {secondsLeft}s — loading coin data...
                      </div>
                    </>
                  ) : phase === 'finished' ? (
                    <>
                      <div className="text-5xl animate-pulse">⏳</div>
                      <div className="text-lg font-semibold" style={{ color: '#a78bfa' }}>
                        Round ending...
                      </div>
                      <div className="text-sm" style={{ color: '#7c6b9c' }}>
                        Settling the previous round — new lottery starts shortly
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-5xl animate-spin" style={{ animationDuration: '3s' }}>🎰</div>
                      <div className="text-lg font-semibold" style={{ color: '#a78bfa' }}>
                        Lottery in progress...
                      </div>
                      <div className="text-sm" style={{ color: '#7c6b9c' }}>
                        Spinning the wheel for this round's coins — stand by
                      </div>
                      {secondsLeft > 0 && (
                        <div className="text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}>
                          Reveal in {secondsLeft}s
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : phase === 'lottery' ? (
                <LotteryPanel
                  racers={racers}
                  userPick={userPick}
                  secondsLeft={secondsLeft}
                />
              ) : phase === 'reveal' ? (
                <RevealPanel
                  round={round}
                  racers={liveRacers}
                  secondsLeft={secondsLeft}
                />
              ) : phase === 'racing' ? (
                <RacePanel
                  round={round}
                  racers={liveRacers}
                  userPick={userPick}
                  secondsLeft={secondsLeft}
                  choosingOpen={choosingOpen}
                />
              ) : phase === 'finished' ? (
                <FinishedPanel
                  round={round}
                  racers={racers}
                  userPick={userPick}
                  secondsLeft={secondsUntilNextRound}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="text-5xl animate-pulse">⏳</div>
                  <div className="text-lg font-semibold" style={{ color: '#a78bfa' }}>
                    Next race starting soon...
                  </div>
                </div>
              )}
            </div>

            {/* Pick distribution */}
            {round && picks.length > 0 && liveRacers.length > 0 && (
              <div
                className="mt-4 rounded-2xl p-4"
                style={{
                  background: 'rgba(26,10,46,0.5)',
                  border: '1px solid rgba(167,139,250,0.15)',
                }}
              >
                <div className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: '#a78bfa' }}>
                  🗳️ Pick Distribution ({picks.length} total)
                </div>
                <div className="flex flex-wrap gap-2">
                  {liveRacers.map((racer, i) => {
                    const count = picks.filter((p) => p.coinId === racer.coinId).length;
                    const pct = picks.length > 0 ? Math.round((count / picks.length) * 100) : 0;
                    const color = ['#ff3da8','#fbbf24','#34d399','#60a5fa','#a78bfa','#fb7185'][i % 6];
                    return (
                      <div
                        key={racer.coinId}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl"
                        style={{ background: `${color}18`, color, border: `1.5px solid ${color}44` }}
                      >
                        {racer.imageUrl && (
                          <img src={racer.imageUrl} alt="" className="w-4 h-4 rounded-full" />
                        )}
                        <span className="font-semibold">${racer.symbol}</span>
                        <span className="opacity-70">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Leaderboard />

            {/* How to play */}
            <div
              className="rounded-2xl p-4 space-y-2"
              style={{
                background: 'rgba(26,10,46,0.5)',
                border: '1px solid rgba(167,139,250,0.15)',
              }}
            >
              <div className="font-bold text-sm" style={{ color: '#ff3da8' }}>
                🎮 How to Play
              </div>
              <div className="space-y-1.5 text-xs" style={{ color: '#a78bfa' }}>
                <div>🎰 <strong className="text-white/80">Lottery (30s)</strong> — Random coins selected</div>
                <div>👀 <strong className="text-white/80">Reveal (30s)</strong> — See your racers before the gun</div>
                <div>🏁 <strong className="text-white/80">Race (13 min)</strong> — Picking opens, live prices battle</div>
                <div>🔒 <strong className="text-white/80">Picks lock</strong> — 5 min before race ends</div>
                <div>🏆 <strong className="text-white/80">Win</strong> — Top gainer earns you +100 pts</div>
                <div>💜 <strong className="text-white/80">Participate</strong> — Everyone earns +10 pts</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HomePage;
